/* ------------------------------------------------------------------
 * home-analytics.js
 *
 * Instruments the home page (templates/index.json) with two events,
 * dual-piped (window.dataLayer + window.Shopify.analytics.publish)
 * matching the pattern from mobile-nav.liquid + search-modal.liquid:
 *
 *   home_section_view      — fired once per section per pageview when
 *                            >=50% of the section enters the viewport
 *   home_hero_cta_click    — fired on any <a href> click inside a hero
 *                            section ([data-section-type^="image-hero"]
 *                            or [data-section-type="video-hero"])
 *
 * Gating: only runs on body.template-index. Self-noops elsewhere.
 * No DOM mutations on other templates — pure read + listener attach.
 * ------------------------------------------------------------------ */
(function () {
  if (!document.body || !document.body.classList) return;
  if (!document.body.classList.contains('template-index')) return;

  /* Guard against double-execution. If for any reason this script is
   * loaded or evaluated twice on the same page, the second IIFE would
   * have its own sections[] and viewed{} state — every home_section_view
   * would fire twice. The flag below makes the second run a no-op. */
  if (window.__rastahHomeAnalyticsInit) return;
  window.__rastahHomeAnalyticsInit = true;

  /* Defer the actual indexing + observer setup until the DOM is fully
   * settled. Even with `defer`, this script was historically running at
   * "interactive" readyState — before Stiletto's lazy section hydration
   * had attached the home sections under <main>. That left the section
   * catalog at 0 entries and no IO observers were registered, so
   * home_section_view never fired in the wild.
   *
   * Strategy: if DOM is still parsing, wait for DOMContentLoaded; once
   * we have a parsed DOM, run a first pass. If that pass finds 0
   * sections (the lazy-hydration case), retry on window.load + a
   * MutationObserver on <body> as a safety net for late-attached
   * sections. */
  function whenReady(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  }

  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (e) {}
  }

  var pageLoadTs = Date.now();
  var sections = [];
  var seenWrapperIds = {};
  var io = null;
  var indexedOnce = false;

  /* ---- home_section_view ----------------------------------------- */
  var viewed = {};
  function emitView(s) {
    if (viewed[s.index]) return;
    viewed[s.index] = true;
    track('home_section_view', {
      section_index: s.index,
      section_type: s.sectionType,
      section_heading: s.heading,
      time_to_view_ms: Date.now() - pageLoadTs
    });
  }

  /* Walk every element with [data-section-id][data-section-type] and
   * collapse to its outer <div id="shopify-section-..."> wrapper. We
   * de-dupe by wrapper id so nested data-section-type descendants
   * (e.g. countdown-bar inside image-hero-with-countdown) don't
   * double-count.
   *
   * Filter: only TEMPLATE sections (templates/index.json) — globals
   * like header/announcement-bar/footer/mobile-nav/search-modal/
   * quick-cart are rendered via {% section %} in layout/theme.liquid
   * and tracked under their own analytics families (mobile_nav_*,
   * search_modal_*, etc.). Tracking them here would (a) double-count
   * traffic that's already instrumented elsewhere and (b) fire
   * multiple home_section_view events on first paint for sections
   * that aren't home-merchandising decisions.
   * Shopify renders template sections with id like
   * `shopify-section-template--{template_id}__{section_key}` — the
   * `template--` substring is the reliable identifier.
   *
   * Re-callable: idempotent, only catalogs sections not seen before. */
  function indexSections() {
    var marked = document.querySelectorAll('[data-section-id][data-section-type]');
    var newCount = 0;
    for (var i = 0; i < marked.length; i++) {
      var el = marked[i];
      var wrapper = el.closest('[id^="shopify-section-"]');
      if (!wrapper) continue;
      if (wrapper.id.indexOf('template--') < 0) continue;
      if (seenWrapperIds[wrapper.id]) continue;
      seenWrapperIds[wrapper.id] = true;

      var headingEl = wrapper.querySelector('h1, h2, h3');
      var heading = headingEl ? (headingEl.textContent || '').trim().slice(0, 80) : '';
      var rect = wrapper.getBoundingClientRect();

      var s = {
        el: wrapper,
        sectionId: el.getAttribute('data-section-id') || '',
        sectionType: el.getAttribute('data-section-type') || '',
        heading: heading,
        initiallyAboveFold: rect.top < window.innerHeight && rect.bottom > 0,
        index: sections.length + 1
      };
      s.isHero = /^image-hero/i.test(s.sectionType) || s.sectionType === 'video-hero';
      s.el.setAttribute('data-home-section-index', s.index);
      s.el.setAttribute('data-home-section-type', s.sectionType);
      sections.push(s);
      newCount++;

      /* Observe the new section if our IO is already set up */
      if (io) io.observe(s.el);
    }
    return newCount;
  }

  function setupObserver() {
    if (io) return;
    if (!('IntersectionObserver' in window)) {
      /* IO unsupported — fire all known sections immediately */
      sections.forEach(emitView);
      return;
    }
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = parseInt(entry.target.getAttribute('data-home-section-index'), 10);
        var s = sections[idx - 1];
        if (s) emitView(s);
      });
    }, { threshold: 0.5 });
    sections.forEach(function (s) { io.observe(s.el); });
  }

  function bootstrap() {
    if (indexedOnce) return;
    indexedOnce = true;
    indexSections();
    setupObserver();

    /* If first pass found 0 sections (lazy hydration), retry on window
     * load AND set up a body MutationObserver as a backup. The MO disconnects
     * after it has indexed something to avoid permanent CPU cost. */
    if (sections.length === 0) {
      window.addEventListener('load', function () {
        if (indexSections() > 0) setupObserver();
      }, { once: true });

      if ('MutationObserver' in window) {
        var mo = new MutationObserver(function () {
          if (indexSections() > 0) {
            setupObserver();
            mo.disconnect();
          }
        });
        mo.observe(document.body, { childList: true, subtree: true });
        /* Hard stop after 10s — don't observe forever */
        setTimeout(function () { mo.disconnect(); }, 10000);
      }
    }
  }

  /* ---- home_hero_cta_click --------------------------------------- */
  function scrollDepthPct() {
    var docH = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    );
    if (!docH) return 0;
    return Math.min(100, Math.round(((window.scrollY + window.innerHeight) / docH) * 100));
  }

  /* Click delegation is safe to attach immediately — it just reads from
   * the sections array via [data-home-section-index]. If the array is
   * empty at click time, the handler returns early. */
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest && e.target.closest('a[href]');
    if (!link) return;
    var heroWrapper = link.closest('[data-home-section-index]');
    if (!heroWrapper) return;
    var idx = parseInt(heroWrapper.getAttribute('data-home-section-index'), 10);
    var s = sections[idx - 1];
    if (!s || !s.isHero) return;
    track('home_hero_cta_click', {
      hero_index: s.index,
      hero_heading: s.heading,
      cta_label: (link.textContent || '').trim().slice(0, 80),
      cta_href: link.getAttribute('href') || '',
      cta_position: s.initiallyAboveFold ? 'above_fold' : 'below_fold',
      scroll_depth_pct_at_click: scrollDepthPct(),
      ts_since_load_ms: Date.now() - pageLoadTs
    });
  }, true);

  whenReady(bootstrap);
})();
